import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  createUserWithEmailAndPassword,
  deleteUser,
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendPasswordResetEmail,
  getAdditionalUserInfo,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  unlink,
  updatePassword as firebaseUpdatePassword,
  updateProfile,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, onSnapshot, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { AnalyticsEvents, trackEvent } from '../analytics/events';
import { auth, db, isFirebaseConfigured } from '../config/firebase';
import { parseCollectionCopyLanguage, type CollectionCopyLanguage } from '../utils/collectionLanguage';
import { normalizeStoredCountryCode } from '../constants/profileCountries';
import {
  DEFAULT_DISPLAY_CURRENCY,
  normalizeDisplayCurrency,
  type DisplayCurrency,
} from '../constants/displayCurrencies';
import {
  DEFAULT_MARKET_PRICE_PROVIDER,
  normalizeMarketPriceProvider,
  type MarketPriceProvider,
} from '../constants/marketProviders';
import { normalizeSocialLinks, type SocialLinks } from '../constants/socialPlatforms';
import { publicDirectoryDoc } from '../firebase/collections';
import {
  syncPublicCollectionListing,
  resolveAvailablePlayerHandle,
  buildInitialPlayerSlug,
  type PublicCollectionProfile,
} from '../utils/publicCollectionSync';
import { renameDeckOwnerHandle } from './useDeck';
import type { CollectionVisibility } from '../types';
import type { CollectionSummary } from '../utils/collectionSummary';
import { collectionSummariesEqual, parseCollectionSummary } from '../utils/collectionSummary';
import { CollectionSummaryProvider } from './useCollectionSummary';
import {
  mergeUserDocLiveFields,
  resolveCollectionVisibility,
  resolveTeamId,
} from './userDocLiveFields';

export interface RiftboundUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  avatarUrl: string | null;
  backgroundImageUrl: string | null;
  bio: string;
  socialLinks: SocialLinks;
  slug: string;
  /** ISO 3166-1 alpha-2 when set; null means "International" / unspecified. */
  countryCode: string | null;
  /** Whether this user's collection is publicly readable via their share link. */
  collectionVisibility: CollectionVisibility;
  /**
   * When true, the collection page counts English, Chinese, and Korean copies
   * separately. New accounts start on, so the language note is visible.
   * A saved false keeps one combined collection.
   */
  collectionLanguageEnabled: boolean;
  /** Language a new copy gets while the collection page is showing All. */
  collectionDefaultLanguage: CollectionCopyLanguage;
  /** Current team id when the user belongs to a team; null otherwise. */
  teamId: string | null;
  /** All binders (set + custom). Null until first binders page or migrate. */
  binderCount: number | null;
  /** Preferred cached market-price source shown across cards, decks, and collection values. */
  marketPriceProvider: MarketPriceProvider;
  /** Currency used to display market prices. Quotes stay in the marketplace currency. */
  displayCurrency: DisplayCurrency;
  authProvider: 'email' | 'google';
  /** All linked provider IDs on this account, e.g. ['password', 'google.com']. */
  linkedProviders: string[];
  /**
   * True only on accounts created after the profile prompt shipped, until the
   * player saves or closes that prompt. Existing accounts omit the field.
   */
  profileSetupPending: boolean;
  /** Per-user feature flags written by admins in Firestore. */
  features: {
    deckBuilder?: boolean;
    /** Card scanner / mobile camera-based collection input. Private beta. */
    scanner?: boolean;
  };
}

interface AuthContextValue {
  user: RiftboundUser | null;
  loading: boolean;
  /** Email+password sign-in */
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  updateDisplayName: (name: string) => Promise<void>;
  /** Set whether the user's collection is publicly readable. */
  setCollectionVisibility: (visibility: CollectionVisibility) => Promise<void>;
  /** Show All / English / 中文 / 한국어 on the collection page. */
  setCollectionLanguageEnabled: (enabled: boolean) => Promise<void>;
  /** Language saved when adding a copy from the All view. */
  setCollectionDefaultLanguage: (language: CollectionCopyLanguage) => Promise<void>;
  deleteAccount: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  // AlterSearch-compatible aliases used by ported sign-in components
  signInWithCredentials: (email: string, password: string) => Promise<void>;
  signUpWithCredentials: (email: string, password: string, name: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  /** OAuth provider sign-in: pass 'google' */
  signInWithProvider: (provider: string) => Promise<void>;
  updatePlayerHandle: (handle: string) => Promise<void>;
  /** Pass empty string for International / no flag. */
  updateProfileCountry: (countryCode: string) => Promise<void>;
  updateMarketPriceProvider: (provider: MarketPriceProvider) => Promise<void>;
  updateDisplayCurrency: (currency: DisplayCurrency) => Promise<void>;
  updateAvatar: (url: string | null) => Promise<void>;
  updateBanner: (url: string | null) => Promise<void>;
  updateBio: (bio: string) => Promise<void>;
  updateSocialLinks: (links: SocialLinks) => Promise<void>;
  getDisplayImage: () => string | null;
  deleteUserAccount: () => Promise<void>;
  /** Re-authenticates the current user. Pass password for email users; omit for Google-only. */
  reauthenticate: (password?: string) => Promise<void>;
  /** Changes password after re-authenticating with currentPassword. */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Links a Google account to the current user via popup. */
  linkGoogle: () => Promise<void>;
  /** Unlinks the Google provider from the current user. */
  unlinkGoogle: () => Promise<void>;
  /** Adds email/password as a second sign-in method (e.g. for Google-only accounts). */
  linkEmailPassword: (email: string, password: string) => Promise<void>;
  /** Marks the new-player profile prompt as finished so it does not return. */
  dismissProfileSetup: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function buildSlug(firebaseUser: FirebaseUser, existing?: string | null): string {
  return buildInitialPlayerSlug({
    uid: firebaseUser.uid,
    displayName: firebaseUser.displayName,
    email: firebaseUser.email,
    existingSlug: existing,
  });
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** Directory sync must not fail profile field saves (or trigger R2 rollback toasts). */
async function syncPublicListingSafe(
  opts: Parameters<typeof syncPublicCollectionListing>[0],
  context: string,
): Promise<void> {
  try {
    await syncPublicCollectionListing(opts);
  } catch (err) {
    console.warn(`[useAuth] public listing sync failed (${context})`, err);
  }
}

function normalizeBio(value: unknown): string {
  return typeof value === 'string' ? value.trim().slice(0, 300) : '';
}

function buildPublicProfile(input: {
  firebaseUser: FirebaseUser;
  currentUser?: RiftboundUser | null;
  data?: Record<string, unknown> | null;
  overrides?: Partial<PublicCollectionProfile>;
}): PublicCollectionProfile {
  return {
    displayName:
      input.overrides?.displayName ??
      input.currentUser?.displayName ??
      nullableString(input.data?.displayName) ??
      input.firebaseUser.displayName,
    photoURL:
      input.overrides?.photoURL ??
      input.currentUser?.photoURL ??
      nullableString(input.data?.photoURL) ??
      input.firebaseUser.photoURL,
    avatarUrl:
      input.overrides?.avatarUrl ??
      input.currentUser?.avatarUrl ??
      nullableString(input.data?.avatarUrl),
    backgroundImageUrl:
      input.overrides?.backgroundImageUrl ??
      input.currentUser?.backgroundImageUrl ??
      nullableString(input.data?.backgroundImageUrl),
    bio:
      input.overrides?.bio ??
      input.currentUser?.bio ??
      normalizeBio(input.data?.bio),
    socialLinks:
      input.overrides?.socialLinks ??
      input.currentUser?.socialLinks ??
      normalizeSocialLinks(input.data?.socialLinks),
    countryCode:
      input.overrides?.countryCode ??
      input.currentUser?.countryCode ??
      normalizeStoredCountryCode(input.data?.countryCode as string | undefined),
  };
}

function userFromFirebase(firebaseUser: FirebaseUser, extra?: {
  slug?: string;
  collectionVisibility?: CollectionVisibility;
}): RiftboundUser {
  const linkedProviders = firebaseUser.providerData.map(p => p.providerId);
  const authProvider: 'email' | 'google' =
    firebaseUser.providerData[0]?.providerId === 'google.com' ? 'google' : 'email';
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    avatarUrl: null,
    backgroundImageUrl: null,
    bio: '',
    socialLinks: {},
    slug: extra?.slug || buildSlug(firebaseUser),
    countryCode: null,
    collectionVisibility: extra?.collectionVisibility ?? 'private',
    collectionLanguageEnabled: true,
    collectionDefaultLanguage: 'en',
    teamId: null,
    binderCount: null,
    marketPriceProvider: DEFAULT_MARKET_PRICE_PROVIDER,
    displayCurrency: DEFAULT_DISPLAY_CURRENCY,
    authProvider,
    linkedProviders,
    features: {},
    profileSetupPending: false,
  };
}

/** The user profile plus the summary that no longer rides on it. */
interface LoadedUser {
  user: RiftboundUser;
  collectionSummary: CollectionSummary | null;
}

async function loadOrCreateUserDoc(firebaseUser: FirebaseUser): Promise<LoadedUser> {
  try {
    const userRef = doc(db, 'users', firebaseUser.uid);
    const snapshot = await getDoc(userRef);
    const existing = snapshot.exists() ? (snapshot.data() as Record<string, unknown>) : null;

    const slug = buildSlug(firebaseUser, existing?.slug as string | null);
    const collectionVisibility = resolveCollectionVisibility(existing);
    const authProvider: 'email' | 'google' =
      firebaseUser.providerData[0]?.providerId === 'google.com' ? 'google' : 'email';
    const linkedProviders = firebaseUser.providerData.map(p => p.providerId);
    const countryCode = normalizeStoredCountryCode(existing?.countryCode as string | undefined);
    const avatarUrl = nullableString(existing?.avatarUrl);
    const backgroundImageUrl = nullableString(existing?.backgroundImageUrl);
    const bio = normalizeBio(existing?.bio);
    const socialLinks = normalizeSocialLinks(existing?.socialLinks);
    const marketPriceProvider = normalizeMarketPriceProvider(existing?.marketPriceProvider);
    const displayCurrency = normalizeDisplayCurrency(existing?.displayCurrency);

    if (!snapshot.exists()) {
      setDoc(userRef, {
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        avatarUrl: null,
        backgroundImageUrl: null,
        bio: '',
        socialLinks: {},
        slug,
        collectionVisibility: 'private',
        collectionLanguageEnabled: true,
        collectionDefaultLanguage: 'en',
        marketPriceProvider: DEFAULT_MARKET_PRICE_PROVIDER,
        displayCurrency: DEFAULT_DISPLAY_CURRENCY,
        authProvider,
        createdAt: Timestamp.now(),
        profileSetupPending: true,
      }).catch(err => console.warn('[useAuth] setDoc failed (non-fatal)', err));

      // Ensure this user is immediately discoverable by their slug.
      syncPublicCollectionListing({
        uid: firebaseUser.uid,
        slug,
        visibility: 'private',
        profile: {
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          avatarUrl: null,
          backgroundImageUrl: null,
          bio: '',
          socialLinks: {},
          countryCode: null,
        },
      }).catch(err => console.warn('[useAuth] publicDirectory write failed (non-fatal)', err));
    } else if (existing && !('collectionVisibility' in existing) && 'allowDirectoryListing' in existing) {
      // One-time migration: write the new field so the old boolean is no longer needed.
      setDoc(userRef, { collectionVisibility }, { merge: true }).catch(() => {});
    }

    const rawFeatures = existing?.features as Record<string, unknown> | undefined;
    const features: RiftboundUser['features'] = {
      deckBuilder: rawFeatures?.deckBuilder === true,
      scanner: rawFeatures?.scanner === true,
    };

    return {
      user: {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        avatarUrl,
        backgroundImageUrl,
        bio,
        socialLinks,
        slug,
        countryCode,
        collectionVisibility,
        collectionLanguageEnabled: existing?.collectionLanguageEnabled !== false,
        collectionDefaultLanguage: parseCollectionCopyLanguage(existing?.collectionDefaultLanguage),
        teamId: resolveTeamId(existing),
        binderCount: typeof existing?.binderCount === 'number' ? existing.binderCount : null,
        marketPriceProvider,
        displayCurrency,
        authProvider,
        linkedProviders,
        features,
        profileSetupPending: !snapshot.exists() || existing?.profileSetupPending === true,
      },
      collectionSummary: parseCollectionSummary(existing?.collectionSummary),
    };
  } catch (err) {
    console.warn('[useAuth] Firestore unavailable, using auth-only user', err);
    return { user: userFromFirebase(firebaseUser), collectionSummary: null };
  }
}

/** Safari/iOS can leave Auth or Firestore IndexedDB ops pending forever — never block the shell. */
const AUTH_BOOTSTRAP_TIMEOUT_MS = 6_000;
const USER_DOC_TIMEOUT_MS = 8_000;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = window.setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    promise.then(
      (value) => {
        window.clearTimeout(id);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(id);
        reject(err);
      },
    );
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<RiftboundUser | null>(null);
  // Held apart from `user` so a card add does not reallocate the user object
  // and re-subscribe every listener keyed on it.
  const [collectionSummary, setCollectionSummary] = useState<CollectionSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isFirebaseConfigured) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let unsubUserDoc: (() => void) | undefined;
    let settled = false;

    const finishLoading = () => {
      if (cancelled || settled) return;
      settled = true;
      setLoading(false);
    };

    // If WebKit IndexedDB is wedged, onAuthStateChanged may never fire.
    const bootstrapTimer = window.setTimeout(() => {
      console.warn('[useAuth] Auth bootstrap timed out; continuing without a settled session');
      finishLoading();
    }, AUTH_BOOTSTRAP_TIMEOUT_MS);

    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      window.clearTimeout(bootstrapTimer);
      unsubUserDoc?.();
      unsubUserDoc = undefined;
      try {
        if (!fbUser) {
          setUser(null);
          setCollectionSummary(null);
        } else {
          let loaded: LoadedUser;
          try {
            loaded = await withTimeout(
              loadOrCreateUserDoc(fbUser),
              USER_DOC_TIMEOUT_MS,
              'loadOrCreateUserDoc',
            );
          } catch (err) {
            console.warn('[useAuth] User doc load stalled; using auth-only profile', err);
            loaded = { user: userFromFirebase(fbUser), collectionSummary: null };
          }
          if (cancelled) return;
          setUser(loaded.user);
          setCollectionSummary(loaded.collectionSummary);
          // Keep teamId / visibility in sync when invite accept or leave updates the user doc.
          unsubUserDoc = onSnapshot(doc(db, 'users', fbUser.uid), (snap) => {
            if (!snap.exists()) return;
            const data = snap.data() as Record<string, unknown>;
            setUser((prev) => (prev ? mergeUserDocLiveFields(prev, data) : prev));
            const next = parseCollectionSummary(data.collectionSummary);
            setCollectionSummary((prev) =>
              collectionSummariesEqual(prev, next) ? prev : next,
            );
          });
        }
      } finally {
        finishLoading();
      }
    });
    return () => {
      cancelled = true;
      window.clearTimeout(bootstrapTimer);
      unsub();
      unsubUserDoc?.();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!isFirebaseConfigured || !auth) throw new Error('Firebase is not configured. Add VITE_FIREBASE_* to .env.local');
    await signInWithEmailAndPassword(auth, email, password);
    trackEvent(AnalyticsEvents.LOGIN, { method: 'email' });
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, displayName: string) => {
      if (!isFirebaseConfigured || !auth) throw new Error('Firebase is not configured. Add VITE_FIREBASE_* to .env.local');
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
        await updateProfile(cred.user, { displayName });
      }
      trackEvent(AnalyticsEvents.SIGN_UP, { method: 'email' });
    },
    []
  );

  const signInWithGoogle = useCallback(async () => {
    if (!isFirebaseConfigured || !auth) throw new Error('Firebase is not configured. Add VITE_FIREBASE_* to .env.local');
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false;
    trackEvent(isNewUser ? AnalyticsEvents.SIGN_UP : AnalyticsEvents.LOGIN, { method: 'google' });
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
    trackEvent(AnalyticsEvents.LOGOUT);
  }, []);

  const sendPasswordReset = useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  }, []);

  const updateDisplayName = useCallback(async (name: string) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const snapshot = await getDoc(doc(db, 'users', uid));
    const data = snapshot.data() as Record<string, unknown> | undefined;
    const slug = (typeof data?.slug === 'string' ? data.slug : user?.slug) ?? '';
    const visibility = resolveCollectionVisibility(data ?? null);

    await updateProfile(auth.currentUser, { displayName: name });
    await setDoc(
      doc(db, 'users', uid),
      { displayName: name },
      { merge: true }
    );
    await syncPublicCollectionListing({
      uid,
      slug,
      visibility,
      profile: buildPublicProfile({
        firebaseUser: auth.currentUser,
        currentUser: user,
        data,
        overrides: { displayName: name },
      }),
    });
    setUser((prev) => (prev ? { ...prev, displayName: name } : prev));
  }, [user]);

  const setCollectionLanguageEnabled = useCallback(async (enabled: boolean) => {
    if (!auth.currentUser) return;
    await setDoc(
      doc(db, 'users', auth.currentUser.uid),
      { collectionLanguageEnabled: enabled },
      { merge: true },
    );
    setUser((prev) => (prev ? { ...prev, collectionLanguageEnabled: enabled } : prev));
  }, []);

  const setCollectionDefaultLanguage = useCallback(async (language: CollectionCopyLanguage) => {
    if (!auth.currentUser) return;
    const collectionDefaultLanguage = parseCollectionCopyLanguage(language);
    await setDoc(
      doc(db, 'users', auth.currentUser.uid),
      { collectionDefaultLanguage },
      { merge: true },
    );
    setUser((prev) => (prev ? { ...prev, collectionDefaultLanguage } : prev));
  }, []);

  const setCollectionVisibility = useCallback(async (visibility: CollectionVisibility) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const snapshot = await getDoc(doc(db, 'users', uid));
    const data = snapshot.data();
    const slug = (typeof data?.slug === 'string' ? data.slug : user?.slug) ?? '';
    const teamId =
      (typeof data?.teamId === 'string' && data.teamId) || user?.teamId || null;

    // Persist privacy on the user doc first — this is the source of truth in the app.
    await setDoc(doc(db, 'users', uid), { collectionVisibility: visibility }, { merge: true });

    // publicCollections gate + directory listing. Directory failures must not
    // roll back / toast-fail the privacy change (common with stale slug ownership
    // or legacy directory field shapes that fail rules validation).
    try {
      await syncPublicCollectionListing({
        uid,
        slug,
        visibility,
        profile: buildPublicProfile({
          firebaseUser: auth.currentUser,
          currentUser: user,
          data,
        }),
      });
    } catch (err) {
      console.warn('[useAuth] public listing sync on visibility change failed', err);
    }

    if (teamId) {
      try {
        const {
          loadUserCollectionMap,
          syncTeamSharedCollection,
          visibilitySharesWithTeam,
        } = await import('../features/teams/utils/teamSharedCollectionSync');
        const { runTransaction, Timestamp } = await import('firebase/firestore');
        const { teamDoc } = await import('../firebase/collections');
        const { deriveAdminUids, deriveMemberUids } = await import('../features/teams/types');
        const share = visibilitySharesWithTeam(visibility);
        await runTransaction(db, async (tx) => {
          const tRef = teamDoc(teamId);
          const teamSnap = await tx.get(tRef);
          if (!teamSnap.exists()) return;
          const teamData = teamSnap.data() as {
            members: Array<Record<string, unknown> & { uid: string; shareCollection: boolean }>;
          };
          if (!teamData.members.some((m) => m.uid === uid)) return;
          const members = teamData.members.map((m) =>
            m.uid === uid ? { ...m, shareCollection: share } : m
          );
          tx.set(
            tRef,
            {
              members,
              memberUids: deriveMemberUids(members as never),
              adminUids: deriveAdminUids(members as never),
              updatedAt: Timestamp.now(),
            },
            { merge: true }
          );
        });
        const entries = await loadUserCollectionMap(uid);
        await syncTeamSharedCollection({
          teamId,
          uid,
          displayName: user?.displayName,
          slug: user?.slug,
          entries,
          visibility,
          shareCollection: share,
          revision: collectionSummary?.revision,
        });
      } catch (err) {
        console.warn('[useAuth] team collection sync on visibility change failed', err);
      }
    }

    setUser((prev) => (prev ? { ...prev, collectionVisibility: visibility } : prev));
    trackEvent(AnalyticsEvents.COLLECTION_VISIBILITY, { visibility });
  }, [user, collectionSummary?.revision]);

  const deleteAccount = useCallback(async () => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const snapshot = await getDoc(doc(db, 'users', uid));
    const slug = typeof snapshot.data()?.slug === 'string' ? snapshot.data()!.slug : '';
    if (slug) await deleteDoc(publicDirectoryDoc(slug)).catch(() => {});
    await syncPublicCollectionListing({
      uid,
      slug,
      visibility: 'private',
      profile: {
        displayName: null,
        photoURL: null,
        avatarUrl: null,
        backgroundImageUrl: null,
        bio: '',
        socialLinks: {},
        countryCode: null,
      },
      skipDirectoryWrite: true,
    });
    await deleteDoc(doc(db, 'users', uid));
    await deleteUser(auth.currentUser);
    trackEvent(AnalyticsEvents.ACCOUNT_DELETE);
  }, []);

  const getIdToken = useCallback(async () => {
    if (!auth.currentUser) return null;
    return auth.currentUser.getIdToken();
  }, []);

  // AlterSearch-compatible aliases
  const signInWithCredentials = signIn;
  const signUpWithCredentials = signUp;
  const forgotPassword = sendPasswordReset;

  const signInWithProvider = useCallback(async (provider: string) => {
    if (provider === 'google') await signInWithGoogle();
    else throw new Error(`Unsupported provider: ${provider}`);
  }, [signInWithGoogle]);

  const updatePlayerHandle = useCallback(async (handle: string) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const clean = await resolveAvailablePlayerHandle(handle, uid);
    const snapshot = await getDoc(doc(db, 'users', uid));
    const data = snapshot.data();
    const previousSlug = typeof data?.slug === 'string' ? data.slug : user?.slug;
    const currentVisibility: CollectionVisibility = resolveCollectionVisibility(
      data as Record<string, unknown> | null ?? null
    );

    // Directory rename (write new + delete old) before users.slug, so a failed
    // old-slug delete can be retried with the same previousSlug.
    await syncPublicCollectionListing({
      uid,
      slug: clean,
      visibility: currentVisibility,
      previousSlug,
      profile: buildPublicProfile({
        firebaseUser: auth.currentUser,
        currentUser: user,
        data: data as Record<string, unknown> | undefined,
      }),
    });
    await setDoc(doc(db, 'users', uid), { slug: clean, profileSetupPending: false }, { merge: true });
    // Keep ownerHandle on all deck documents in sync with the new slug.
    await renameDeckOwnerHandle(uid, clean);
    setUser((prev) => (prev ? { ...prev, slug: clean, profileSetupPending: false } : prev));
  }, [user]);

  const updateProfileCountry = useCallback(async (countryCode: string) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const normalized = normalizeStoredCountryCode(countryCode);
    const snapshot = await getDoc(doc(db, 'users', uid));
    const data = snapshot.data() as Record<string, unknown> | undefined;
    const slug = (typeof data?.slug === 'string' ? data.slug : user?.slug) ?? '';
    const visibility = resolveCollectionVisibility(data ?? null);
    await setDoc(
      doc(db, 'users', uid),
      { countryCode: normalized },
      { merge: true }
    );
    await syncPublicCollectionListing({
      uid,
      slug,
      visibility,
      profile: buildPublicProfile({
        firebaseUser: auth.currentUser,
        currentUser: user,
        data,
        overrides: { countryCode: normalized },
      }),
    });
    setUser((prev) => (prev ? { ...prev, countryCode: normalized } : prev));
  }, [user]);

  const updateMarketPriceProvider = useCallback(async (provider: MarketPriceProvider) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const normalized = normalizeMarketPriceProvider(provider);
    await setDoc(
      doc(db, 'users', uid),
      { marketPriceProvider: normalized },
      { merge: true }
    );
    setUser((prev) => (prev ? { ...prev, marketPriceProvider: normalized } : prev));
  }, []);

  const updateDisplayCurrency = useCallback(async (currency: DisplayCurrency) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const normalized = normalizeDisplayCurrency(currency);
    await setDoc(
      doc(db, 'users', uid),
      { displayCurrency: normalized },
      { merge: true }
    );
    setUser((prev) => (prev ? { ...prev, displayCurrency: normalized } : prev));
  }, []);

  const updateAvatar = useCallback(async (url: string | null) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const avatarUrl = nullableString(url);
    const snapshot = await getDoc(doc(db, 'users', uid));
    const data = snapshot.data() as Record<string, unknown> | undefined;
    const slug = (typeof data?.slug === 'string' ? data.slug : user?.slug) ?? '';
    const visibility = resolveCollectionVisibility(data ?? null);
    await setDoc(doc(db, 'users', uid), { avatarUrl }, { merge: true });
    await syncPublicListingSafe({
      uid,
      slug,
      visibility,
      profile: buildPublicProfile({
        firebaseUser: auth.currentUser,
        currentUser: user,
        data,
        overrides: { avatarUrl },
      }),
    }, 'avatar');
    setUser(prev => prev ? { ...prev, avatarUrl } : prev);
  }, [user]);

  const updateBanner = useCallback(async (url: string | null) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const backgroundImageUrl = nullableString(url);
    const snapshot = await getDoc(doc(db, 'users', uid));
    const data = snapshot.data() as Record<string, unknown> | undefined;
    const slug = (typeof data?.slug === 'string' ? data.slug : user?.slug) ?? '';
    const visibility = resolveCollectionVisibility(data ?? null);
    await setDoc(doc(db, 'users', uid), { backgroundImageUrl }, { merge: true });
    await syncPublicListingSafe({
      uid,
      slug,
      visibility,
      profile: buildPublicProfile({
        firebaseUser: auth.currentUser,
        currentUser: user,
        data,
        overrides: { backgroundImageUrl },
      }),
    }, 'banner');
    setUser(prev => prev ? { ...prev, backgroundImageUrl } : prev);
  }, [user]);

  const updateBio = useCallback(async (value: string) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const bio = normalizeBio(value);
    const snapshot = await getDoc(doc(db, 'users', uid));
    const data = snapshot.data() as Record<string, unknown> | undefined;
    const slug = (typeof data?.slug === 'string' ? data.slug : user?.slug) ?? '';
    const visibility = resolveCollectionVisibility(data ?? null);
    await setDoc(doc(db, 'users', uid), { bio }, { merge: true });
    await syncPublicCollectionListing({
      uid,
      slug,
      visibility,
      profile: buildPublicProfile({
        firebaseUser: auth.currentUser,
        currentUser: user,
        data,
        overrides: { bio },
      }),
    });
    setUser(prev => prev ? { ...prev, bio } : prev);
  }, [user]);

  const updateSocialLinks = useCallback(async (value: SocialLinks) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const socialLinks = normalizeSocialLinks(value);
    const snapshot = await getDoc(doc(db, 'users', uid));
    const data = snapshot.data() as Record<string, unknown> | undefined;
    const slug = (typeof data?.slug === 'string' ? data.slug : user?.slug) ?? '';
    const visibility = resolveCollectionVisibility(data ?? null);
    await setDoc(doc(db, 'users', uid), { socialLinks }, { merge: true });
    await syncPublicCollectionListing({
      uid,
      slug,
      visibility,
      profile: buildPublicProfile({
        firebaseUser: auth.currentUser,
        currentUser: user,
        data,
        overrides: { socialLinks },
      }),
    });
    setUser(prev => prev ? { ...prev, socialLinks } : prev);
  }, [user]);

  const getDisplayImage = useCallback((): string | null => {
    return user?.avatarUrl ?? user?.photoURL ?? null;
  }, [user?.avatarUrl, user?.photoURL]);

  const deleteUserAccount = deleteAccount;

  const reauthenticate = useCallback(async (password?: string) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('No authenticated user');
    if (password !== undefined) {
      if (!currentUser.email) throw new Error('No email on account');
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);
    } else {
      const provider = new GoogleAuthProvider();
      await reauthenticateWithPopup(currentUser, provider);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) throw new Error('No authenticated user');
    const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
    await reauthenticateWithCredential(currentUser, credential);
    await firebaseUpdatePassword(currentUser, newPassword);
  }, []);

  const linkGoogle = useCallback(async () => {
    if (!auth.currentUser) throw new Error('No authenticated user');
    const provider = new GoogleAuthProvider();
    const result = await linkWithPopup(auth.currentUser, provider);
    const linkedProviders = result.user.providerData.map(p => p.providerId);
    setUser(prev => prev ? { ...prev, linkedProviders } : prev);
  }, []);

  const unlinkGoogle = useCallback(async () => {
    if (!auth.currentUser) throw new Error('No authenticated user');
    const updated = await unlink(auth.currentUser, 'google.com');
    const linkedProviders = updated.providerData.map(p => p.providerId);
    const authProvider: 'email' | 'google' = linkedProviders.includes('google.com') ? 'google' : 'email';
    setUser(prev => prev ? { ...prev, linkedProviders, authProvider } : prev);
  }, []);

  const linkEmailPassword = useCallback(async (email: string, password: string) => {
    if (!auth.currentUser) throw new Error('No authenticated user');
    const credential = EmailAuthProvider.credential(email, password);
    const result = await linkWithCredential(auth.currentUser, credential);
    const linkedProviders = result.user.providerData.map(p => p.providerId);
    await setDoc(doc(db, 'users', auth.currentUser.uid), { authProvider: 'email' }, { merge: true });
    setUser(prev => prev ? { ...prev, linkedProviders } : prev);
  }, []);

  const dismissProfileSetup = useCallback(async () => {
    setUser((prev) => (prev?.profileSetupPending ? { ...prev, profileSetupPending: false } : prev));
    if (!auth.currentUser) return;
    try {
      await setDoc(
        doc(db, 'users', auth.currentUser.uid),
        { profileSetupPending: false },
        { merge: true },
      );
    } catch (err) {
      console.warn('[useAuth] profile setup dismiss failed', err);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    sendPasswordReset,
    updateDisplayName,
    setCollectionVisibility,
    setCollectionLanguageEnabled,
    setCollectionDefaultLanguage,
    deleteAccount,
    getIdToken,
    signInWithCredentials,
    signUpWithCredentials,
    forgotPassword,
    signInWithProvider,
    updatePlayerHandle,
    updateProfileCountry,
    updateMarketPriceProvider,
    updateDisplayCurrency,
    updateAvatar,
    updateBanner,
    updateBio,
    updateSocialLinks,
    getDisplayImage,
    deleteUserAccount,
    reauthenticate,
    changePassword,
    linkGoogle,
    unlinkGoogle,
    linkEmailPassword,
    dismissProfileSetup,
  };

  return (
    <AuthContext.Provider value={value}>
      <CollectionSummaryProvider value={collectionSummary}>
        {children}
      </CollectionSummaryProvider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
